import { Calendar, Contact, Eye, Hash, ToggleLeft, Type } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useIntl } from '@/core/i18n/use-intl';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Switch,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/shared/components/ui';
import { cn } from '@/shared/utils';
import type { JsonSchemaType, MetadataField } from './types';
import { jsonSchemaToBuilderState } from './utils';

export interface MetadataFormPreviewHeader {
  title: string;
  description?: string;
}

interface MetadataFormPreviewProps {
  schema?: JsonSchemaType;
  className?: string;
  header?: MetadataFormPreviewHeader | false;
}

const typeIcons: Record<string, React.ComponentType<{ className?: string }>> = {
  string: Type,
  number: Hash,
  boolean: ToggleLeft,
  date: Calendar,
  contact: Contact,
};

function ContactPreviewFields() {
  const { t } = useIntl();

  return (
    <div className="grid grid-cols-2 gap-4">
      <div className="space-y-2">
        <Label className="text-sm font-medium">{t('metadata.contact.name')}</Label>
        <Input placeholder={t('metadata.contact.namePlaceholder')} />
      </div>
      <div className="space-y-2">
        <Label className="text-sm font-medium">{t('metadata.contact.email')}</Label>
        <Input type="email" placeholder={t('metadata.contact.emailPlaceholder')} />
      </div>
      <div className="space-y-2">
        <Label className="text-sm font-medium">{t('metadata.contact.phone1')}</Label>
        <Input type="tel" placeholder={t('metadata.contact.phonePlaceholder')} />
      </div>
      <div className="space-y-2">
        <Label className="text-sm font-medium">{t('metadata.contact.phone2')}</Label>
        <Input type="tel" placeholder={t('metadata.contact.phonePlaceholder')} />
      </div>
    </div>
  );
}

function PreviewField({ field, locale }: { field: MetadataField; locale: string }) {
  const { t } = useIntl();
  const isGerman = locale === 'de-DE';
  const label = isGerman ? field.labelDe : field.labelEn || field.labelDe;
  const placeholder = isGerman ? field.labelEn : field.labelDe;
  const [checked, setChecked] = useState(false);

  // Section fields render as visual separators
  if (field.type === 'section') {
    return (
      <div className="col-span-6 mt-4 mb-2 pb-2 border-b border-border">
        <h4 className="text-sm font-semibold text-foreground">{label || 'Unbenannt'}</h4>
      </div>
    );
  }

  // Contact fields render as a card with sub-fields
  if (field.type === 'contact') {
    return (
      <div className="col-span-6">
        <Card className="border-border/60">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-base font-medium">
              <Contact className="size-4 text-muted-foreground" />
              {label || 'Unbenannt'}
              {field.required && <span className="text-destructive">*</span>}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <ContactPreviewFields />

            <div className="ml-6 pl-4 border-l-2 border-muted">
              <p className="text-sm font-medium mb-3">{t('metadata.contact.fallbackContact')}</p>
              <p className="text-sm text-muted-foreground mb-3">
                {t('metadata.contact.fallbackDescription')}
              </p>
              <ContactPreviewFields />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const widthClass =
    {
      '33': 'col-span-6 md:col-span-2',
      '50': 'col-span-6 md:col-span-3',
      '100': 'col-span-6',
    }[field.width] || 'col-span-6 md:col-span-2';

  const TypeIcon = typeIcons[field.type] || Type;

  return (
    <div className={cn(widthClass, 'flex flex-col')}>
      <Label className="mb-1.5 text-sm font-medium text-foreground">
        {label || 'Unbenannt'}
        {field.required && <span className="text-destructive ml-1">*</span>}
      </Label>
      <div className="relative">
        {field.type === 'boolean' ? (
          <div className="flex items-center gap-2 h-9">
            <Switch checked={checked} onCheckedChange={setChecked} />
            <span className="text-sm text-muted-foreground">{checked ? 'Ja' : 'Nein'}</span>
          </div>
        ) : (
          <div className="relative">
            <Input
              type={field.type === 'date' ? 'date' : field.type === 'number' ? 'number' : 'text'}
              placeholder={placeholder}
            />
            <TypeIcon className="absolute right-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground/50 pointer-events-none" />
          </div>
        )}
      </div>
    </div>
  );
}

export function MetadataFormPreview({ schema, className, header }: MetadataFormPreviewProps) {
  const { t, locale } = useIntl();
  const [activeTab, setActiveTab] = useState<string | undefined>(undefined);

  const { groups, fields } = useMemo(() => jsonSchemaToBuilderState(schema), [schema]);

  // Set initial active tab
  const effectiveActiveTab = activeTab || groups[0]?.id;

  const sortedGroups = useMemo(() => [...groups].sort((a, b) => a.order - b.order), [groups]);

  const fieldsByGroup = useMemo(() => {
    const map: Record<string, MetadataField[]> = {};
    for (const group of groups) {
      map[group.id] = fields
        .filter((f) => f.groupId === group.id)
        .sort((a, b) => a.order - b.order);
    }
    return map;
  }, [groups, fields]);

  // Determine header content
  const showHeader = header !== false;
  const headerTitle = header ? header.title : t('metadata.builder.preview');
  const headerDescription = header ? header.description : t('metadata.builder.previewDescription');

  if (groups.length === 0 || fields.length === 0) {
    return (
      <div
        className={cn(
          'flex flex-col h-full bg-card rounded-lg border shadow-sm overflow-hidden',
          className
        )}
      >
        {showHeader && (
          <div className="border-b p-5">
            <h2 className="text-base font-semibold">{headerTitle}</h2>
            {headerDescription && (
              <p className="text-sm text-muted-foreground">{headerDescription}</p>
            )}
          </div>
        )}
        {/* Preview indicator when no header */}
        {!showHeader && (
          <div className="h-11 border-b px-5 flex items-center shrink-0">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Eye className="size-4" />
              <span className="text-xs font-medium uppercase tracking-wider">
                {t('metadata.builder.preview')}
              </span>
            </div>
          </div>
        )}
        <div className="flex-1 flex items-center justify-center text-muted-foreground">
          {t('metadata.builder.noFieldsToPreview')}
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'flex flex-col h-full bg-card rounded-lg border shadow-sm overflow-hidden',
        className
      )}
    >
      {/* Header - optional */}
      {showHeader && (
        <div className="border-b p-5">
          <h2 className="text-base font-semibold">{headerTitle}</h2>
          {headerDescription && (
            <p className="text-sm text-muted-foreground">{headerDescription}</p>
          )}
        </div>
      )}

      {/* Content */}
      <div className="flex-1 flex flex-col min-h-0">
        <Tabs
          value={effectiveActiveTab}
          onValueChange={setActiveTab}
          className="flex-1 flex flex-col"
        >
          {/* Tabs header - with preview indicator when no header */}
          <div className="h-11 border-b px-5 flex items-center justify-between">
            <TabsList className="h-full bg-transparent p-0 gap-4 flex items-end -mb-px">
              {sortedGroups.map((group) => (
                <TabsTrigger
                  key={group.id}
                  value={group.id}
                  className="px-0 pb-2 pt-2 data-[state=active]:bg-transparent data-[state=active]:shadow-none rounded-none border-b-2 border-transparent data-[state=active]:border-primary"
                >
                  {locale === 'de-DE' ? group.labelDe : group.labelEn || group.labelDe}
                </TabsTrigger>
              ))}
            </TabsList>
            {/* Preview indicator when no header */}
            {!showHeader && (
              <div className="flex items-center gap-1.5 text-muted-foreground">
                <Eye className="size-3.5" />
                <span className="text-xs font-medium uppercase tracking-wider">
                  {t('metadata.builder.preview')}
                </span>
              </div>
            )}
          </div>

          {/* Tab Content */}
          {sortedGroups.map((group) => (
            <TabsContent key={group.id} value={group.id} className="flex-1 overflow-y-auto p-5 m-0">
              <div className="max-w-3xl mx-auto">
                <div className="grid grid-cols-6 gap-x-4 gap-y-4">
                  {fieldsByGroup[group.id]?.map((field) => (
                    <PreviewField key={field.id} field={field} locale={locale} />
                  ))}

                  {(!fieldsByGroup[group.id] || fieldsByGroup[group.id].length === 0) && (
                    <div className="col-span-full text-center py-8 text-muted-foreground italic bg-muted/30 rounded-lg border border-dashed">
                      {t('metadata.builder.noFieldsInGroup')}
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </div>
  );
}
